
import { Helmet } from 'react-helmet';
import env from '@/config/env';

interface AppHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  noIndex?: boolean;
  children?: React.ReactNode;
}

/**
 * Global app metadata and branding component
 */
export function AppHead({
  title,
  description = "Compare salaries and company reviews on Saalari",
  canonicalUrl,
  ogImage = "/lovable-uploads/8a820b64-0f78-43c0-93b3-a9a4a5ce17cd.png",
  noIndex = false,
  children
}: AppHeadProps) {
  const baseUrl = "https://saalari.com";
  const fullUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : undefined;
  const pageTitle = title ? `${title} | ${env.appName}` : env.appName;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#9b87f5" />
      <meta name="color-scheme" content="light" />
      <meta name="application-name" content={env.appName} />
      <meta name="apple-mobile-web-app-title" content={env.appName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Canonical URL */}
      {fullUrl && <link rel="canonical" href={fullUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={env.appName} />
      {fullUrl && <meta property="og:url" content={fullUrl} />}
      {ogImage && <meta property="og:image" content={ogImage.startsWith("http") ? ogImage : `${baseUrl}${ogImage}`} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage.startsWith("http") ? ogImage : `${baseUrl}${ogImage}`} />}
      
      {/* No index directive if needed */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* PWA related tags */}
      <link rel="manifest" href="/manifest.json" />
      <link rel="icon" href="/lovable-uploads/338d4bd7-a5e5-4f3b-bcf1-a0c8cbcfe867.png" type="image/png" />
      <link rel="apple-touch-icon" href="/lovable-uploads/338d4bd7-a5e5-4f3b-bcf1-a0c8cbcfe867.png" />
      
      {/* Additional head elements */}
      {children}
    </Helmet>
  );
}
